import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/utils/currency';
import { ExpenseActionsCell } from './ExpenseActionsCell';
import type { Expense, ExpenseStatus } from '../types';

const STATUS_BADGE_CLASS: Record<ExpenseStatus, string> = {
  NEW: 'border border-slate-200 bg-slate-100 text-slate-700',
  PENDING: 'border border-amber-200 bg-amber-50 text-amber-700',
  ACCEPTED: 'border border-green-200 bg-green-100 text-green-700',
  DENIED: 'border border-red-200 bg-red-50 text-red-700',
};

interface ExpenseColumnsProps {
  onEdit?: ((expense: Expense) => void) | undefined;
  hasAccess?: ((resource: string, action: string) => boolean) | undefined;
}

export function useExpenseColumns({
  onEdit,
  hasAccess = () => true,
}: ExpenseColumnsProps = {}): ColumnDef<Expense>[] {
  const { t } = useTranslation();

  const canUpdate = hasAccess('expense', 'update');
  const canAccept = hasAccess('expense', 'activate');
  const canReject = hasAccess('expense', 'reject');
  const canPending = hasAccess('expense', 'pending');
  const canDelete = hasAccess('expense', 'delete');

  const columns: ColumnDef<Expense>[] = [
    {
      accessorKey: 'expenseCategory',
      header: () => <p>{t('expense.categoryLabel')}</p>,
      cell: ({ row }) => <p>{row.original?.expenseCategory?.name ?? '—'}</p>,
    },
    {
      accessorKey: 'matter',
      header: () => <p>{t('expense.matterLabel')}</p>,
      cell: ({ row }) => <p>{row.original?.matter?.name ?? '—'}</p>,
    },
    {
      accessorKey: 'contract',
      header: () => <p>{t('expense.contractLabel')}</p>,
      cell: ({ row }) => <p>{row.original?.contract?.name ?? '—'}</p>,
    },
    {
      accessorKey: 'rate',
      header: () => <p className="text-right">{t('expense.rateLabel')}</p>,
      cell: ({ row }) => (
        <p className="text-right">
          {formatCurrency(row.original?.rate, { currency: row.original?.currency })}
        </p>
      ),
    },
    {
      accessorKey: 'quantity',
      header: () => <p className="text-right">{t('expense.quantityLabel')}</p>,
      cell: ({ row }) => <p className="text-right">{row.original?.quantity ?? 0}</p>,
    },
    {
      accessorKey: 'amount',
      header: () => <p className="text-right">{t('expense.amountLabel')}</p>,
      cell: ({ row }) => (
        <p className="text-right font-medium">
          {formatCurrency(row.original?.amount, { currency: row.original?.currency })}
        </p>
      ),
    },
    {
      accessorKey: 'currency',
      header: () => <p>{t('expense.currencyLabel')}</p>,
      cell: ({ row }) => <p>{row.original?.currency ?? '—'}</p>,
    },
    {
      accessorKey: 'status',
      header: () => <p>{t('expense.statusLabel')}</p>,
      cell: ({ row }) => {
        const status = row.original?.status ?? 'NEW';
        return <Badge className={STATUS_BADGE_CLASS[status]}>{t(`expense.status.${status}`)}</Badge>;
      },
    },
    {
      accessorKey: 'createdBy',
      header: () => <p>{t('expense.createdByLabel')}</p>,
      cell: ({ row }) => <p>{row.original?.createdBy?.name ?? '—'}</p>,
    },
    {
      accessorKey: 'createdAt',
      header: () => <p>{t('createdAt')}</p>,
      cell: ({ row }) => {
        const value = row.original?.createdAt;
        return <p>{value ? format(new Date(value), 'yyyy-MM-dd') : '—'}</p>;
      },
    },
  ];

  if (canUpdate || canAccept || canReject || canPending || canDelete) {
    columns.splice(0, 0, {
      id: 'actions',
      header: () => t('actionsLabel'),
      cell: ({ row }) => <ExpenseActionsCell expense={row.original} onEdit={onEdit} />,
    });
  }

  return columns;
}
